<template>
    <div id="product-parameter" class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div id="product-parameter-modal-header" class="modal-header border-bottom-0 bg-transparent" data-bs-theme="dark">
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div id="product-parameter-modal-body" class="modal-body p-5">
            <div>
              <div class="row mt-1">
                <div v-if="!selectedModel?.product?.name?.toLowerCase().includes('s-cover')" class="col">
                  <el-form-item prop="heightWithoutDeduction">
                    <div class="col">
                      <label for="height-range" class="el-form-item__label mb-0">{{ $t('components.order-form.height') }}</label>
                      <el-slider id="height-range" v-model="orderData.heightWithoutDeduction" :min="100" :max="500" />
                      <el-input-number v-model="orderData.heightWithoutDeduction" />
                    </div>
                  </el-form-item>
                </div>
              </div>

              <div class="row mt-1">
                <div v-if="!selectedModel?.product?.name?.toLowerCase().includes('s-cover')" class="col">
                  <el-form-item prop="depth">
                    <div class="col">
                      <label for="width-range" class="el-form-item__label mb-0">{{ $t('components.order-form.scope-top') }}</label>
                      <el-slider id="width-range" v-model="orderData.depth" :min="150" :max="600" />
                      <el-input-number v-model="orderData.depth" />
                    </div>
                  </el-form-item>
                </div>
              </div>

              <div class="row mt-1">
                <div v-if="!selectedModel?.product?.name?.toLowerCase().includes('s-cover')" class="col">
                  <el-form-item prop="calfCircumferenceWithoutDeduction">
                    <div class="col">
                      <label for="scope-range" class="el-form-item__label mb-0">
                        {{  selectedModel?.product?.name?.toLowerCase().includes('prothesenschaft')
                            ? $t('components.order-form.scope-bottom-prothesenschaft')
                            : $t('components.order-form.scope-bottom') }}
                      </label>
                      <el-slider id="height-range" v-model="orderData.calfCircumferenceWithoutDeduction" :min="100" :max="460" />
                      <el-input-number v-model="orderData.calfCircumferenceWithoutDeduction" />
                    </div>
                  </el-form-item>
                </div>
              </div>
            </div>
          </div> 
          <div class="row d-flex justify-content-center gap-2 mb-3 mt-2">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" :disabled="selectedModel?.product?.name?.toLowerCase().includes('prothesenschaft') || !selectedModel?.product?.name?.toLowerCase().includes('u-flex')" @click="handleCoverDeformationRequest">
              {{ $t('components.mesh-editor.startDeformation') }}
            </button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="handleDeformationRequest">
              {{ $t('components.mesh-editor.startTemplateDeformation') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script lang="ts">
  import { ref } from 'vue';
  import { Component, Vue, Prop } from 'vue-facing-decorator';
  import { Modal } from 'bootstrap';
  import type { UploadStatus } from 'element-plus/es/components/upload/src/upload';
  import Auth from '@/services/api/auth';
  import { Model2 } from '@/types/api/Model/Model';
  import ProductParameter from '@/components/productParameters/ProductParameter.vue';
  import { TableField } from '@/utils/file';
  
  interface UploadData {
    name: string;
    url: string;
    uid: number;
    status?: UploadStatus;
    base64?: string;
  }
  
  interface OrderData {
    technicianFirstname: string;
    technicianLastname: string;
    technician: string;
    company: string;
    customerNr: string;
    orderNr: string;
    billingNr: string;
    commissionNo: string;
    vrNumber: string;
    vgNumber: string;
    vsgNumber: string;
    mail: string;
    coverModel: string;
    prothesisType: string;
    bodySide: string;
    kneeJoint: string;
    foot: string;
    zip: string;
    sewInByCustomer: boolean;
    cosmeticEndingAtFoot: boolean;
    design: string;
    individualDesign: boolean;
    designFiles: UploadData[];
    heightWithoutDeduction: number;
    heightDeduction: number;
    heightWithDeduction: number;
    depth: number;
    depthRight: number;
    bilateralAmputee: boolean;
    calfCircumferenceWithoutDeduction: number;
    calfCircumferenceDeduction: number;
    calfCircumferenceWithDeduction: number;
    ankleCircumference: number;
    imageFiles: UploadData[];
    scanFiles: UploadData[];
    remarks: string;
    isPrivacy: boolean;
    privacy: string;
    balconyType: string;
    mountingType: string;
    paneelType: string;
    orientationType: string;
    fenceType: string;
    color: string;
    mountingProfileType: string;
    lightCoverType: string;
    iPRatingType: string;
    lightColorType: string;
    lightIntensityType: string;
    vFour: boolean;
    eloxed: boolean;
    lightLength: number;
    bathtubType: string;
    wallpanels: boolean;
    antiSlip: boolean;
    panelVariant: string;
    singleType: string[];
    comboType: string;
    panelWidth: number;
    angle: number;
    angleRight: number;
    angleLeft: number;
    varnish: string;
  }
  
  @Component({
    methods: {
      ref,
    },
    components: {
      ProductParameter,
    },
    emits: ['zipChanged', 'updateProductListState', 'handleShowMeshEditor', 'convertToUrl', 'coverDeformationResult', 'deformationResult', 
      'isLoadingDeformation'
    ],
  })
  export default class ProductParameterModalDialog extends Vue {
    @Prop() selectedModel!: Model2 | null;
    @Prop() authentication!: Auth;
    @Prop() orderData!: OrderData;
    @Prop() videoMetadata!: TableField;
    @Prop() productParameterEmbossing?: boolean;
    deformationAbortController = new AbortController();

    initModal() {
      return new Modal('#product-parameter', {});
    }

    async handleDeformationRequest(){
      const requestOptions = await this.getDeformationRequest();
      const apiUrl = import.meta.env.VITE_MODELLING_API_URL;

      try{
        let response: Response;
        if(this.selectedModel?.product?.name?.toLowerCase().includes('prothesenschaft')){
          response = await fetch(`${apiUrl}/lower_leg_deformation`, requestOptions);
        }
        else{
          response = await fetch(`${apiUrl}/template_leg_deformation`, requestOptions);
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.$emit('deformationResult', data.result_mesh);
        this.$emit('isLoadingDeformation', false);
      } catch (error) {
        console.error('Error during deformation request:', error);
        this.$emit('isLoadingDeformation', false);
      }
    }

    async handleCoverDeformationRequest(){
      const requestOptions = await this.getDeformationRequest();
      const apiUrl = import.meta.env.VITE_MODELLING_API_URL;
      try{
        let response: Response;
        response = await fetch(`${apiUrl}/cover_deformation`, requestOptions);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.$emit('coverDeformationResult', data.result_cover, data.result_template);
        this.$emit('isLoadingDeformation', false);
      } catch (error) {
        console.error('Error during deformation request:', error);
        this.$emit('isLoadingDeformation', false);
      }
    }

    abortDeformation(){
      this.deformationAbortController.abort()
    }

    async getDeformationRequest(){
      this.$emit('isLoadingDeformation', true);
      const height = this.orderData.heightWithoutDeduction;
      const circumCalf = this.orderData.depth;
      const circumAnkle = this.orderData.calfCircumferenceWithoutDeduction

      this.deformationAbortController = new AbortController()

      const requestOptions = {
        method: 'POST',
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          height: height,
          calf_circumference: circumCalf,
          ankle_circumference: circumAnkle,
        }),
        signal: this.deformationAbortController.signal
      };

      return requestOptions;
    }

  }
  </script>
  
  <style lang="scss" scoped>
  #components-modal-header,
  #product-details-modal-header,
  #product-parameter-modal-header {
    background-color: transparent;
    z-index: 1;
  }
  
  #product-details-modal-body,
  #product-parameter-modal-body {
    margin-top: -3.6rem;
    z-index: 0;
  }
  .fixed-bottom {
    padding-right: 0 !important;
  }
  .demonstration {
    opacity: 0;
  }
  .mesh-deformation.show {
    visibility: visible !important;
  }
  .mesh-deformation {
    visibility: hidden;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    bottom: 0;
    top: 0;
    z-index: 100 !important;
    background-color: #00000066;
    //padding: 40px 70px;
  }
  .mesh-deformation-view {
    overflow: hidden; /* Menghilangkan scroll */
    position: relative;
    min-height: 100%;
    background-color: #fff;
    // border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    //max-width: 90%; /* Batas maksimum lebar */
    //max-height: 90%; /* Batas maksimum tinggi */
    margin: auto; /* Memastikan elemen tetap di tengah */
    flex: 1;
  }
  .mesh-deformation-info {
    // padding: ;
    position: absolute;
    left: 20px;
    top: 20px;
    z-index: 101 !important;
    border-color: transparent;
    padding: 20px;
    text-align: left !important;
  }
  .mesh-deformation-title {
    position: absolute;
    left: 20px;
    bottom: 20px;
    z-index: 101 !important;
  }
  .mesh-deformation-progress {
    position: absolute;
    z-index: 101 !important;
    width: 100px !important;
    height: 100px !important;
    border-radius: 10px !important;
    background-color: transparent !important;
  }
  .splitView {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    display: flex;
    width: 100%;
    height: 100%;
    gap: 10px;
  }
  
  .column {
    position: relative;
    // flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #ccc;
    overflow: hidden;
    border-radius: 14px;
  }

  .modal-content {
    overflow: hidden;
    border: none;
    width: 40rem;
  }

  .btn-close {
    z-index: 1;
    --bs-btn-close-color: black;
  }

  .btn-primary {
    background-color: var(--color-primary);
    color: white;
  }

  .btn-primary:hover {
    background-color: var(--color-primary);
    opacity: 0.8;
  }

  .el-input-number {
    width: 100%;
  }
  </style>